// DelegatedAgentDrawer: one delegated agent's whole conversation, read-only
// (Chief of Staff mode; plan design/2026-10-26-chief-of-staff-agents-panel.md,
// T6). Opened from the Agents list or a sub-agent card, over the right side at
// the Insights drawer's width, so the chat stays in view beside it.
//
// Two sources, one look (house rule: every pane conversation is SidePaneChat):
//  - a sub-agent's conversation is in its live tool call (`toolCall.subagent`,
//    filled by the reducer), drawn through `subagentToMessages`;
//  - a workflow agent's is only in the owner's home on the runner, read by
//    `useWorkflowTranscript` and drawn through `transcriptToMessages`.
//
// Persistent, not modal: the chat and its panel stay usable while it is open.
// Esc closes it.

import React, { useEffect, useMemo, useRef } from 'react'
import { Alert, Box, Button, Drawer, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SidePaneChat, { type SidePaneConversation } from './SidePaneChat.js'
import { DelegatedStatusBadge, formatUsage } from './SubagentCard.js'
import { CARD_RADIUS, HAIRLINE } from './surface.js'
import { deriveDelegatedAgents, subagentToMessages, transcriptToMessages } from '../delegatedAgents.js'
import { useWorkflowRuns, useWorkflowTranscript } from '../hooks/useWorkflowAgents.js'
import type { AgentMessage, ArtifactInfo, DelegatedStatus, DelegatedUsage } from '../types.js'
import { INSIGHTS_DRAWER_WIDTH } from '../../components/chat/ChatDrawer'

/** Which delegated agent to show: a direct sub-agent, or one agent of a workflow run. */
export type AgentRef =
  | { kind: 'subagent'; toolCallId: string }
  | { kind: 'workflow-agent'; toolCallId: string; runId: string; agentId: string }

export interface DelegatedAgentDrawerProps {
  /** The agent to show; null: closed. */
  agentRef: AgentRef | null
  /** The chat's messages: where a sub-agent's conversation and a workflow's status are. */
  messages: AgentMessage[]
  sessionId: string
  apiBaseUrl?: string
  authHeader?: string
  /** The chat's artifacts: the ones this agent made are drawn at the end of its conversation. */
  artifacts?: ArtifactInfo[]
  /** Space kept free on the right (the open right-hand panel's width), so the Agents list stays in view beside the drawer. */
  rightOffset?: number
  onClose: () => void
}

/**
 * The agent's own files as one last row, drawn by the row's artifact cards
 * (the main conversation never shows them: agentEventReducer leaves a
 * delegated agent's files unpinned).
 */
function withFiles(rows: AgentMessage[], key: string, files: ArtifactInfo[]): AgentMessage[] {
  if (files.length === 0) return rows
  return [...rows, { id: `${key}:files`, role: 'assistant', content: '', timestamp: '', autoArtifactPaths: files.map(f => f.filePath) }]
}

const NO_FILES: ArtifactInfo[] = []

const NOOP = () => {}

const TRUNCATED_LINE = 'Earlier lines are not shown: the conversation is longer than this view keeps.'
const NOT_SAVED_LINE = 'This agent\'s conversation is no longer saved.'

interface FrameProps {
  title: string
  kind: string
  status: DelegatedStatus
  usage?: DelegatedUsage
  notice?: React.ReactNode
  conversation?: SidePaneConversation
  onClose: () => void
}

/** The header, a notice line, and the conversation with no composer. */
function Frame({ title, kind, status, usage, notice, conversation, onClose }: FrameProps) {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: HAIRLINE, flexShrink: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {usage ? `${kind} · ${formatUsage(usage)}` : kind}
          </Typography>
        </Box>
        <DelegatedStatusBadge status={status} />
        <IconButton size="small" aria-label="Close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      {notice && <Box sx={{ px: 2, pt: 2, flexShrink: 0 }}>{notice}</Box>}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {conversation && <SidePaneChat readOnly conversation={conversation} loading={false} />}
      </Box>
    </>
  )
}

function noticeText(text: string) {
  return <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>{text}</Typography>
}

interface BodyProps extends Omit<DelegatedAgentDrawerProps, 'agentRef'> {}

function SubagentBody({ toolCallId, messages, sessionId, apiBaseUrl, authHeader, artifacts = NO_FILES, onClose }: BodyProps & { toolCallId: string }) {
  const entry = useMemo(
    () => deriveDelegatedAgents(messages).find(e => e.toolCallId === toolCallId && e.kind === 'subagent'),
    [messages, toolCallId],
  )
  const toolCall = entry?.toolCall
  const files = useMemo(() => artifacts.filter(a => a.parentToolUseId === toolCallId), [artifacts, toolCallId])
  const rows = useMemo(() => withFiles(toolCall ? subagentToMessages(toolCall) : [], toolCallId, files), [toolCall, toolCallId, files])
  const info = entry?.kind === 'subagent' ? entry.info : undefined
  const conversation = useMemo<SidePaneConversation>(() => ({
    messages: rows,
    sessionId,
    isStreaming: info?.status === 'running',
    apiBaseUrl,
    authHeader,
    artifacts: files,
    onAnswerQuestion: NOOP,
    onSendMessage: NOOP,
  }), [rows, sessionId, info?.status, apiBaseUrl, authHeader, files])

  if (!info) {
    return <Frame title="Sub-agent" kind="Sub-agent" status="stopped" notice={noticeText(NOT_SAVED_LINE)} onClose={onClose} />
  }
  return (
    <Frame
      title={info.description || info.agentType || 'Sub-agent'}
      kind={info.agentType ? `Sub-agent · ${info.agentType}` : 'Sub-agent'}
      status={info.status}
      usage={info.usage}
      conversation={conversation}
      onClose={onClose}
    />
  )
}

function WorkflowAgentBody({ toolCallId, runId, agentId, messages, sessionId, apiBaseUrl, authHeader, artifacts = NO_FILES, onClose }: BodyProps & { toolCallId: string; runId: string; agentId: string }) {
  const workflow = useMemo(() => {
    const e = deriveDelegatedAgents(messages).find(x => x.toolCallId === toolCallId)
    return e?.kind === 'workflow' ? e.info : undefined
  }, [messages, toolCallId])
  const workflowRunning = workflow?.status === 'running'
  // The same shared poll the Agents list reads: no second request.
  const { runs } = useWorkflowRuns(sessionId, [{ runId, running: workflowRunning }])
  const agent = runs.get(runId)?.agents.find(a => a.id === agentId)

  // A result means Done; no result while the workflow runs means Working;
  // no result once it has settled takes the workflow's own status, and a
  // workflow no longer in the messages is not polled as running.
  const status: DelegatedStatus = agent?.result != null || agent?.status === 'completed'
    ? 'completed'
    : workflowRunning ? 'running' : workflow?.status ?? 'stopped'

  const { transcript, error, notFound, retry } = useWorkflowTranscript(sessionId, runId, agentId, status === 'running')
  const files = useMemo(() => artifacts.filter(a => a.agentId === agentId), [artifacts, agentId])
  const rows = useMemo(() => withFiles(transcript ? transcriptToMessages(transcript) : [], agentId, files), [transcript, agentId, files])
  const conversation = useMemo<SidePaneConversation>(() => ({
    messages: rows,
    sessionId,
    isStreaming: status === 'running',
    apiBaseUrl,
    authHeader,
    artifacts: files,
    onAnswerQuestion: NOOP,
    onSendMessage: NOOP,
  }), [rows, sessionId, status, apiBaseUrl, authHeader, files])

  let notice: React.ReactNode = null
  if (error) {
    notice = (
      <Alert
        severity="error"
        sx={{ borderRadius: CARD_RADIUS }}
        action={<Button color="info" size="small" onClick={retry} sx={{ textTransform: 'none' }}>Retry</Button>}
      >
        {error}
      </Alert>
    )
  } else if (notFound) {
    notice = noticeText(NOT_SAVED_LINE)
  } else if (!transcript) {
    notice = noticeText('Loading…')
  } else if (transcript.truncated) {
    notice = noticeText(TRUNCATED_LINE)
  }

  const phase = agent?.phase ? ` · ${agent.phase}` : ''
  return (
    <Frame
      title={agent?.label || agentId}
      kind={`Workflow${workflow?.name ? ` ${workflow.name}` : ''}${phase}`}
      status={status}
      notice={notice}
      conversation={transcript ? conversation : undefined}
      onClose={onClose}
    />
  )
}

export default function DelegatedAgentDrawer({ agentRef, onClose, rightOffset = 0, ...rest }: DelegatedAgentDrawerProps) {
  const open = agentRef !== null

  // Esc closes it. The drawer is persistent (no modal to catch the key), so
  // the listener is the document's while it is open.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useEffect(() => {
    if (!open) return
    // Marked so the chat's double-Escape stop does not count it (it listens on
    // window, after this document listener).
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={open}
      onClose={onClose}
      PaperProps={{ 'data-testid': 'delegated-agent-drawer' } as Record<string, string>}
      sx={{
        '& .MuiDrawer-paper': {
          width: INSIGHTS_DRAWER_WIDTH.width,
          minWidth: INSIGHTS_DRAWER_WIDTH.minWidth,
          maxWidth: INSIGHTS_DRAWER_WIDTH.maxWidth,
          height: '100vh',
          right: rightOffset,
          borderRight: rightOffset ? '1px solid' : undefined,
          borderRightColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        },
      }}
    >
      {agentRef?.kind === 'subagent' && (
        <SubagentBody key={agentRef.toolCallId} toolCallId={agentRef.toolCallId} onClose={onClose} {...rest} />
      )}
      {agentRef?.kind === 'workflow-agent' && (
        <WorkflowAgentBody
          key={`${agentRef.runId}/${agentRef.agentId}`}
          toolCallId={agentRef.toolCallId}
          runId={agentRef.runId}
          agentId={agentRef.agentId}
          onClose={onClose}
          {...rest}
        />
      )}
    </Drawer>
  )
}
